import { FC, useMemo } from 'react'
// => Tiptap packages
import { EditorContent, generateJSON, useEditor, type JSONContent } from '@tiptap/react'
import Document from '@tiptap/extension-document'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import Link from '@tiptap/extension-link'
import Bold from '@tiptap/extension-bold'
import { BulletList, ListItem, OrderedList } from '@tiptap/extension-list'
import Underline from '@tiptap/extension-underline'
import Italic from '@tiptap/extension-italic'
import Strike from '@tiptap/extension-strike'
import HardBreak from '@tiptap/extension-hard-break'

import DOMPurify from 'dompurify'

import { ALLOWED_ATTR, ALLOWED_TAGS } from './utils'

interface RichTextEditorDisplayProps {
  content: string
  maxLength?: number
}

const EXTENSIONS = [
  Document,
  Paragraph,
  HardBreak,
  Text,
  Link.configure({
    openOnClick: false,
  }),
  Bold,
  Underline,
  Italic,
  Strike,
  OrderedList,
  BulletList,
  ListItem,
]

const truncateEditorContent = (content: JSONContent, maxLength: number) => {
  let length = 0
  const traverse = (node: JSONContent | null | undefined): JSONContent | null => {
    if (!node || length >= maxLength) return null
    if (node.type === 'text' && node.text) {
      if (length + node.text.length > maxLength) {
        node.text = node.text.slice(0, maxLength - length) + '...' // Truncate
      }
      length += node.text.length
    } else if (node.content) {
      node.content = node.content
        .map(traverse)
        .filter((child): child is JSONContent => child !== null)
    }
    return node
  }
  return traverse(structuredClone(content)) // Deep copy before modification
}

export const RichTextEditorDisplay: FC<RichTextEditorDisplayProps> = ({ content, maxLength }) => {
  const editorDocument = useMemo(() => {
    const sanitizedContent = DOMPurify.sanitize(content, {
      ALLOWED_TAGS: ALLOWED_TAGS,
      ALLOWED_ATTR: ALLOWED_ATTR,
    })
    const fullDocument = generateJSON(sanitizedContent, EXTENSIONS)
    return maxLength ? truncateEditorContent(fullDocument, maxLength) : fullDocument
  }, [content, maxLength])
  const editor = useEditor(
    { content: editorDocument, extensions: EXTENSIONS, editable: false, immediatelyRender: true },
    [editorDocument],
  )
  return <EditorContent editor={editor} />
}
